import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/store/ProductCard";
import { JsonLd, useOgTags } from "@/components/store/SeoJsonLd";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const OCCASIONS = ["Daily Wear", "Everyday", "Festive", "Wedding", "Gifting"];
const PURITIES = ["9KT", "925", "14KT", "18K", "22K", "999"];
const COLOURS = ["Yellow", "Rose", "White"];
const SORTS = [
  ["featured", "Featured"], ["price_asc", "Price: Low to High"], ["price_desc", "Price: High to Low"],
  ["newest", "Newest"], ["carat", "Carat"],
];
const PAGE_SIZE = 24;
const FILTER_DEFAULTS = { metal: "all", purity: "all", occasion: "all", colour: "all", style: "", minPrice: "", maxPrice: "", minCarat: "", maxCarat: "", inStock: false, personalised: false };

export default function Collection() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [collection, setCollection] = useState(null);
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [sheetOpen, setSheetOpen] = useState(false);

  const readFilters = useCallback(() => ({
    sort: searchParams.get("sort") || "featured",
    metal: searchParams.get("metal") || "all",
    purity: searchParams.get("purity") || "all",
    occasion: searchParams.get("occasion") || "all",
    colour: searchParams.get("colour") || "all",
    style: searchParams.get("style") || "",
    minPrice: searchParams.get("min_price") || "",
    maxPrice: searchParams.get("max_price") || "",
    minCarat: searchParams.get("min_carat") || "",
    maxCarat: searchParams.get("max_carat") || "",
    inStock: searchParams.get("in_stock") === "true",
    personalised: searchParams.get("personalised") === "true",
  }), [searchParams]);

  const filters = useMemo(readFilters, [readFilters]);
  const q = searchParams.get("q") || "";

  // URL is the source of truth — every filter change is shareable/indexable
  const set = (k, v) => {
    const next = new URLSearchParams(searchParams);
    const key = { minPrice: "min_price", maxPrice: "max_price", minCarat: "min_carat", maxCarat: "max_carat", inStock: "in_stock" }[k] || k;
    const isDefault = v === "all" || v === "" || v === false || (k === "sort" && v === "featured");
    if (isDefault) next.delete(key); else next.set(key, String(v));
    setSearchParams(next, { replace: false });
    setPage(1);
  };

  const clear = () => {
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    setSearchParams(next);
    setPage(1);
  };

  useEffect(() => {
    if (!slug) { setCollection(null); return; }
    api.get(`/collections/${slug}`).then((r) => setCollection(r.data)).catch(() => setCollection(null));
  }, [slug]);

  const fetchPage = useCallback((pageNo, append) => {
    setLoading(true);
    const params = { sort: filters.sort, limit: PAGE_SIZE, page: pageNo };
    if (slug) params.collection = slug;
    if (q) params.q = q;
    if (filters.metal !== "all") params.metal = filters.metal;
    if (filters.purity !== "all") params.purity = filters.purity;
    if (filters.occasion !== "all") params.occasion = filters.occasion;
    if (filters.colour !== "all") params.colour = filters.colour;
    if (filters.style) params.style = filters.style;
    if (filters.personalised) params.personalised = true;
    if (filters.inStock) params.in_stock = true;
    if (filters.minPrice) params.min_price = Number(filters.minPrice);
    if (filters.maxPrice) params.max_price = Number(filters.maxPrice);
    if (filters.minCarat) params.min_carat = Number(filters.minCarat);
    if (filters.maxCarat) params.max_carat = Number(filters.maxCarat);
    api.get("/products", { params })
      .then((r) => setData((d) => ({
        total: r.data.total, pages: r.data.pages,
        items: append ? [...d.items, ...r.data.items] : r.data.items,
      })))
      .catch(() => setData({ items: [], total: 0, pages: 1 }))
      .finally(() => setLoading(false));
  }, [filters, slug, q]);

  useEffect(() => { fetchPage(page, page > 1); }, [fetchPage, page]);

  // Canonical rules: only designated facet pages are indexable. Filtered browse
  // views canonicalise to the parent and carry noindex — no self-cannibalisation.
  useEffect(() => {
    const isFiltered = Boolean(q) || Object.entries(filters).some(([k, v]) =>
      (k !== "sort" && v !== "all" && v !== "" && v !== false) || (k === "sort" && v !== "featured"));
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) { robots = document.createElement("meta"); robots.name = "robots"; document.head.appendChild(robots); }
    robots.content = isFiltered ? "noindex, follow" : "index, follow";
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.rel = "canonical"; document.head.appendChild(link); }
    link.href = `https://siyaara.com${slug ? `/collections/${slug}` : "/shop"}`;
    return () => { robots.content = "index, follow"; };
  }, [filters, q, slug]);

  const applied = useMemo(() => {
    const chips = [];
    const label = { metal: "Metal", purity: "Purity", occasion: "Occasion", colour: "Colour", style: "Style" };
    for (const k of ["metal", "purity", "occasion", "colour", "style"]) {
      if (filters[k] && filters[k] !== "all") chips.push({ k, label: `${label[k]}: ${filters[k]}` });
    }
    if (filters.minPrice || filters.maxPrice) chips.push({ k: "_price", label: `₹${filters.minPrice || 0}–₹${filters.maxPrice || "∞"}` });
    if (filters.minCarat || filters.maxCarat) chips.push({ k: "_carat", label: `${filters.minCarat || 0}–${filters.maxCarat || "∞"} ct` });
    if (filters.inStock) chips.push({ k: "inStock", label: "In stock" });
    if (filters.personalised) chips.push({ k: "personalised", label: "Personalisable" });
    return chips;
  }, [filters]);

  const removeChip = (k) => {
    if (k === "_price") { set("minPrice", ""); set("maxPrice", ""); return; }
    if (k === "_carat") { set("minCarat", ""); set("maxCarat", ""); return; }
    if (k === "inStock" || k === "personalised") { set(k, false); return; }
    set(k, k === "style" ? "" : "all");
  };

  const title = q ? `Results for “${q}”` : filters.personalised && !slug ? "Personalised Jewellery" : collection?.name || "All Jewellery";

  useOgTags({
    title: `${title} | Siyaara`,
    description: collection?.description || `Shop ${title.toLowerCase()} — live-priced jewellery with full breakup, BIS hallmarking and insured delivery across India.`,
    url: `https://siyaara.com${slug ? `/collections/${slug}` : "/shop"}`,
  });

  const itemListLd = data.items.length > 0 ? {
    "@context": "https://schema.org", "@type": "ItemList",
    name: title, numberOfItems: data.total,
    itemListElement: data.items.slice(0, 20).map((p, i) => ({
      "@type": "ListItem", position: i + 1, url: `https://siyaara.com/products/${p.slug}`, name: p.title,
    })),
  } : null;

  const filterControls = (
    <div className="space-y-5" data-testid="filter-rail">
      {[
        ["Metal", "metal", ["all", "gold", "silver"], (v) => (v === "all" ? "All metals" : v[0].toUpperCase() + v.slice(1))],
        ["Colour", "colour", ["all", ...COLOURS], (v) => (v === "all" ? "All colours" : `${v} gold`)],
        ["Purity", "purity", ["all", ...PURITIES], (v) => (v === "all" ? "All purity" : v)],
        ["Occasion", "occasion", ["all", ...OCCASIONS], (v) => (v === "all" ? "All occasions" : v)],
      ].map(([label, key, values, fmt]) => (
        <div key={key}>
          <p className="text-[11px] font-mono uppercase tracking-widest text-stone-500 mb-2">{label}</p>
          <div className="flex flex-wrap gap-1.5">
            {values.map((v) => (
              <button key={v} onClick={() => set(key, v)}
                className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${filters[key] === v ? "bg-brand text-ivory border-brand" : "border-stone-300 hover:border-brand"}`}
                data-testid={`filter-${key}-${String(v).toLowerCase()}`}>
                {fmt(v)}
              </button>
            ))}
          </div>
        </div>
      ))}
      <div>
        <p className="text-[11px] font-mono uppercase tracking-widest text-stone-500 mb-2">Live price (₹)</p>
        <div className="flex items-center gap-1.5">
          <Input type="number" placeholder="Min" value={filters.minPrice} onChange={(e) => set("minPrice", e.target.value)} className="w-24 h-9" data-testid="filter-min-price" />
          <Input type="number" placeholder="Max" value={filters.maxPrice} onChange={(e) => set("maxPrice", e.target.value)} className="w-24 h-9" data-testid="filter-max-price" />
        </div>
      </div>
      <div>
        <p className="text-[11px] font-mono uppercase tracking-widest text-stone-500 mb-2">Carats</p>
        <div className="flex items-center gap-1.5">
          <Input type="number" step="0.1" placeholder="Min" value={filters.minCarat} onChange={(e) => set("minCarat", e.target.value)} className="w-24 h-9" data-testid="filter-min-carat" />
          <Input type="number" step="0.1" placeholder="Max" value={filters.maxCarat} onChange={(e) => set("maxCarat", e.target.value)} className="w-24 h-9" data-testid="filter-max-carat" />
        </div>
      </div>
      <div>
        <p className="text-[11px] font-mono uppercase tracking-widest text-stone-500 mb-2">Ring style</p>
        <Input placeholder="e.g. halo, solitaire" value={filters.style} onChange={(e) => set("style", e.target.value)} className="h-9" data-testid="filter-style" />
      </div>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <Switch checked={filters.inStock} onCheckedChange={(v) => set("inStock", v)} data-testid="filter-instock-toggle" /> In stock only
      </label>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <Switch checked={filters.personalised} onCheckedChange={(v) => set("personalised", v)} data-testid="filter-personalised-toggle" /> Personalisable
      </label>
    </div>
  );

  return (
    <div className="container-x py-8" data-testid="collection-page">
      {itemListLd && <JsonLd id="collection-itemlist" data={itemListLd} />}
      <p className="eyebrow">{collection ? "COLLECTION" : "CATALOGUE"}</p>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-1" data-testid="collection-title">{title}</h1>
      {collection?.description && <p className="text-stone-600 mt-2 max-w-2xl">{collection.description}</p>}

      <div className="flex items-center gap-3 mt-6">
        <Button variant="outline" className="lg:hidden" onClick={() => setSheetOpen(true)} data-testid="filters-open-btn">
          <SlidersHorizontal size={15} className="mr-1.5" /> Filters{applied.length ? ` (${applied.length})` : ""}
        </Button>
        {applied.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="applied-chips">
            {applied.map((c) => (
              <button key={c.k} onClick={() => removeChip(c.k)}
                className="flex items-center gap-1 px-2.5 py-1 text-xs bg-brand-light border border-brand/20 rounded-full text-brand"
                data-testid={`chip-${c.k}`}>
                {c.label} <X size={11} />
              </button>
            ))}
            <button onClick={clear} className="text-xs text-stone-500 underline" data-testid="clear-filters-btn">Clear all</button>
          </div>
        )}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs font-mono text-stone-500 hidden sm:inline" data-testid="product-count">{data.total} PIECES</span>
          <Select value={filters.sort} onValueChange={(v) => set("sort", v)}>
            <SelectTrigger className="w-44 h-9" data-testid="sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SORTS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`sort-${v}`}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8 mt-6">
        <aside className="hidden lg:block self-start sticky top-24">{filterControls}</aside>

        <div>
          {loading && page === 1 ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="border border-stone-200 animate-pulse bg-white">
                  <div className="aspect-square bg-stone-100" />
                  <div className="p-4 space-y-2"><div className="h-3 bg-stone-100 w-2/3" /><div className="h-3 bg-stone-100 w-1/3" /></div>
                </div>
              ))}
            </div>
          ) : data.items.length === 0 ? (
            <div className="text-center py-20" data-testid="collection-empty">
              <p className="text-stone-500">No pieces match all those filters. Try removing one.</p>
              <Button variant="outline" className="mt-4" onClick={clear} data-testid="empty-clear-filters-btn">Clear filters</Button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="product-grid">
                {data.items.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
              </div>
              {page < data.pages && (
                <div className="text-center mt-8">
                  <Button variant="outline" onClick={() => setPage(page + 1)} disabled={loading} className="px-8" data-testid="load-more-btn">
                    {loading ? "Loading…" : `Load more (${data.total - data.items.length} left)`}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Mobile filter sheet */}
      {sheetOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="filter-sheet">
          <div className="absolute inset-0 bg-black/40" onClick={() => setSheetOpen(false)} />
          <div className="absolute bottom-0 inset-x-0 bg-white rounded-t-2xl max-h-[80vh] overflow-y-auto p-5 animate-fade-up">
            <div className="flex items-center justify-between mb-4">
              <p className="font-heading font-semibold">Filters</p>
              <button onClick={() => setSheetOpen(false)} aria-label="Close filters" data-testid="filter-sheet-close"><X size={18} /></button>
            </div>
            {filterControls}
            <Button className="w-full mt-6 bg-brand text-ivory" onClick={() => setSheetOpen(false)} data-testid="filter-sheet-apply">
              Show {data.total} pieces
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
